import { useApolloClient } from "@apollo/client";
import { Edit as EditIcon } from "@mui/icons-material";
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { TFnType } from "@msys/tolgee";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslate } from "@tolgee/react";
import {
  namedOperations,
  OfferedProjectType,
} from "../../../clients/graphqlTypes.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import {
  buildCategoryTree,
  type CategoryWithMatchAndChildren,
} from "../../features/categories/helpers.js";
import { useCategories } from "../../features/categories/useCategories.js";
import { EditOverridesModal } from "../../features/organisations/offeredServices/EditOverridesModal.js";
import {
  matrixCellKey,
  OfferedServicesMatrix,
  parseMatrixCellKey,
} from "../../features/organisations/offeredServices/OfferedServicesMatrix.js";
import { OFFERED_PROJECT_TYPES } from "../../features/organisations/offeredServices/projectTypes.js";
import { useProjectTypes } from "../../features/projects/useProjectTypes.js";
import {
  useOrganisationSettingsOfferedServicesQuery,
  useSetOrganisationOfferedServicesMutation,
} from "./OrganisationSettingsOfferedServices.generated.js";

const REFETCH = {
  refetchQueries: [namedOperations.Query.OrganisationSettingsOfferedServices],
  awaitRefetchQueries: true,
};

type ViewTreeNode = {
  id: string;
  name: string;
  rank: number;
  declared: boolean;
  projectTypes: OfferedProjectType[];
  children: ViewTreeNode[];
};

function groupByKey<T, K, V>(
  items: ReadonlyArray<T>,
  getKey: (item: T) => K,
  getValue: (item: T) => V
): Map<K, Set<V>> {
  const map = new Map<K, Set<V>>();
  for (const item of items) {
    const key = getKey(item);
    const value = getValue(item);
    const existing = map.get(key);
    if (existing) existing.add(value);
    else map.set(key, new Set([value]));
  }
  return map;
}

interface Props {
  submenuItems: PageTopbarItem[];
}

export const OrganisationSettingsOfferedServices = ({
  submenuItems,
}: Props) => {
  const { t } = useTranslate([
    "OrganisationPageTopbar",
    "OrganisationProfile",
    "Global",
  ]);
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useOrganisationSettingsOfferedServicesQuery({
    client,
    fetchPolicy: "cache-and-network",
  });
  const organisation = query.data?.viewer?.organisation;

  const [setServices, { loading: saving }] =
    useSetOrganisationOfferedServicesMutation({
      client,
      ...REFETCH,
    });

  const { categories: allCategories } = useCategories();
  const { projectTypeLabels } = useProjectTypes();

  const declaredIds = React.useMemo(
    () => new Set(organisation?.categories ?? []),
    [organisation?.categories]
  );

  const [mode, setMode] = React.useState<"view" | "edit">("view");
  const [pendingDeclared, setPendingDeclared] = React.useState<Set<string>>(
    new Set()
  );
  const [pendingSelections, setPendingSelections] = React.useState<Set<string>>(
    new Set()
  );
  // Nodes the user has collapsed; everything else in the view tree stays open.
  const [viewCollapsed, setViewCollapsed] = React.useState<Set<string>>(
    new Set()
  );
  const [confirmRemovalOpen, setConfirmRemovalOpen] = React.useState(false);
  const [editingOverride, setEditingOverride] = React.useState<{
    categoryId: string;
    projectType: OfferedProjectType;
  } | null>(null);

  const projectTypesByCategory = React.useMemo(
    () =>
      groupByKey(
        organisation?.offeredServices ?? [],
        o => o.categoryId,
        o => o.projectType
      ),
    [organisation]
  );

  const orderedProjectTypes = React.useCallback(
    (categoryId: string): OfferedProjectType[] => {
      const set = projectTypesByCategory.get(categoryId);
      if (!set) return [];
      return OFFERED_PROJECT_TYPES.filter(pt => set.has(pt));
    },
    [projectTypesByCategory]
  );

  const overriddenByCategory = React.useMemo(
    () =>
      groupByKey(
        organisation?.categoryProjectTypeMappingOverrides ?? [],
        o => o.categoryId,
        o => o.projectType
      ),
    [organisation]
  );

  const editingOverrides = React.useMemo(() => {
    if (!organisation || !editingOverride) return [];
    return organisation.categoryProjectTypeMappingOverrides.filter(
      o => o.categoryId === editingOverride.categoryId
    );
  }, [organisation, editingOverride]);

  // Declared categories + their ancestor path. Non-declared ancestors are
  // included so the tree path renders, but flagged for grayed-out display.
  const viewTree = React.useMemo<ViewTreeNode[]>(() => {
    if (allCategories.length === 0 || declaredIds.size === 0) return [];
    const byId = new Map(allCategories.map(c => [c.id, c]));
    const visible = new Set<string>();
    for (const id of declaredIds) {
      let curId: string | null = id;
      while (curId && !visible.has(curId)) {
        visible.add(curId);
        curId = byId.get(curId)?.parentId ?? null;
      }
    }
    const filtered = allCategories
      .filter(c => visible.has(c.id))
      .map(c => ({ ...c, match: undefined }));
    const { categoriesTree } = buildCategoryTree(filtered);
    const toNode = (c: CategoryWithMatchAndChildren): ViewTreeNode => ({
      id: c.id,
      name: c.name,
      rank: c.rank,
      declared: declaredIds.has(c.id),
      projectTypes: orderedProjectTypes(c.id),
      children: (c.children ?? [])
        .slice()
        .sort((a, b) => a.rank - b.rank)
        .map(toNode),
    });
    return categoriesTree
      .slice()
      .sort((a, b) => a.rank - b.rank)
      .map(toNode);
  }, [allCategories, declaredIds, orderedProjectTypes]);

  // Default to fully expanded — the tree only contains declared + ancestors.
  // Honor any nodes the user has explicitly collapsed.
  const viewExpanded = React.useMemo(() => {
    const ids: string[] = [];
    const visit = (nodes: ViewTreeNode[]) => {
      for (const n of nodes) {
        if (!viewCollapsed.has(n.id)) ids.push(n.id);
        visit(n.children);
      }
    };
    visit(viewTree);
    return ids;
  }, [viewTree, viewCollapsed]);

  const declaredRootVerticalIds = React.useMemo(() => {
    if (allCategories.length === 0 || declaredIds.size === 0) {
      return new Set<string>();
    }
    const byId = new Map(allCategories.map(c => [c.id, c]));
    const roots = new Set<string>();
    for (const id of declaredIds) {
      let curId: string | null = id;
      while (curId) {
        const node = byId.get(curId);
        if (!node) break;
        if (!node.parentId) {
          roots.add(curId);
          break;
        }
        curId = node.parentId;
      }
    }
    return roots;
  }, [allCategories, declaredIds]);

  const enterEdit = () => {
    setPendingDeclared(new Set(declaredIds));
    const seedCells = new Set<string>();
    if (organisation) {
      for (const s of organisation.offeredServices) {
        seedCells.add(matrixCellKey(s.categoryId, s.projectType));
      }
    }
    setPendingSelections(seedCells);
    setMode("edit");
  };

  const cancelEdit = () => setMode("view");

  const handleDeclaredChange = React.useCallback((next: readonly string[]) => {
    const nextSet = new Set(next);
    setPendingDeclared(nextSet);
    // Invariant: any cell implies its category is in `declared`.
    setPendingSelections(prev => {
      const filtered = new Set<string>();
      for (const key of prev) {
        if (nextSet.has(parseMatrixCellKey(key).categoryId)) filtered.add(key);
      }
      return filtered;
    });
  }, []);

  const handleToggleCell = React.useCallback(
    (categoryId: string, projectType: OfferedProjectType) => {
      const key = matrixCellKey(categoryId, projectType);
      setPendingSelections(prev => {
        const copy = new Set(prev);
        if (copy.has(key)) copy.delete(key);
        else copy.add(key);
        return copy;
      });
    },
    []
  );

  // Removed categories whose project-type selections would be lost on save.
  // The user is asked to confirm before committing those losses.
  const removedWithServicesNames = React.useMemo(() => {
    if (!organisation) return [] as string[];
    if (mode !== "edit") return [] as string[];
    const byId = new Map(allCategories.map(c => [c.id, c]));
    const removedIds = Array.from(declaredIds).filter(
      id => !pendingDeclared.has(id)
    );
    const removedWithTypes = removedIds.filter(
      id => (projectTypesByCategory.get(id)?.size ?? 0) > 0
    );
    return removedWithTypes.map(id => byId.get(id)?.name ?? id);
  }, [
    organisation,
    mode,
    declaredIds,
    pendingDeclared,
    projectTypesByCategory,
    allCategories,
  ]);

  const executeSave = async () => {
    if (!organisation) return;
    try {
      const categories = Array.from(pendingDeclared);
      const services = Array.from(pendingSelections).map(parseMatrixCellKey);
      await setServices({
        variables: { input: { categories, services } },
      });
      setMode("view");
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    }
  };

  const handleSave = () => {
    if (removedWithServicesNames.length > 0) {
      setConfirmRemovalOpen(true);
    } else {
      void executeSave();
    }
  };

  return (
    <Page
      title={t("Offered services", { ns: "OrganisationPageTopbar" })}
      submenuItems={submenuItems}
    >
      {organisation && (
        <PageContainer>
          <Stack spacing={2}>
            <Box
              sx={{
                alignSelf: "flex-end",
              }}
            >
              {mode === "view" ? (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={enterEdit}
                >
                  {t("Edit", { ns: "Global" })}
                </Button>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {t("Save", { ns: "Global" })}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    {t("Cancel", { ns: "Global" })}
                  </Button>
                </Stack>
              )}
            </Box>

            {mode === "view" && viewTree.length === 0 && (
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("No categories declared yet.", {
                  ns: "OrganisationProfile",
                })}
              </Typography>
            )}

            {mode === "view" && viewTree.length > 0 && (
              <SimpleTreeView
                expandedItems={viewExpanded}
                onExpandedItemsChange={(_, items) => {
                  const open = new Set(items);
                  setViewCollapsed(prev => {
                    const next = new Set(prev);
                    for (const id of viewExpanded) {
                      if (!open.has(id)) next.add(id);
                    }
                    for (const id of items) next.delete(id);
                    return next;
                  });
                }}
                expansionTrigger="iconContainer"
                disableSelection
                itemChildrenIndentation={24}
              >
                {renderViewNodes(viewTree, {
                  t,
                  projectTypeLabels,
                  overriddenByCategory,
                  onEditOverride: (categoryId, projectType) =>
                    setEditingOverride({ categoryId, projectType }),
                })}
              </SimpleTreeView>
            )}

            {mode === "edit" && (
              <OfferedServicesMatrix
                categories={allCategories}
                declaredCategoryIds={pendingDeclared}
                cellSelections={pendingSelections}
                onDeclaredChange={handleDeclaredChange}
                onToggleCell={handleToggleCell}
                initiallyExpandedRootIds={declaredRootVerticalIds}
              />
            )}
          </Stack>
        </PageContainer>
      )}
      {organisation && editingOverride && (
        <EditOverridesModal
          organisationId={organisation.id}
          categoryId={editingOverride.categoryId}
          categoryName={
            allCategories.find(c => c.id === editingOverride.categoryId)
              ?.name ?? ""
          }
          projectType={editingOverride.projectType}
          overrides={editingOverrides}
          onClose={() => setEditingOverride(null)}
        />
      )}
      {confirmRemovalOpen && (
        <ConfirmModal
          title={t("Remove category", { ns: "OrganisationProfile" })}
          text={t(
            "Removing {categories} will also remove all project-type selections within them. Continue?",
            {
              ns: "OrganisationProfile",
              categories: removedWithServicesNames.join(", "),
            }
          )}
          confirmButtonLabel={t("Remove", { ns: "Global" })}
          handleConfirm={executeSave}
          handleClose={() => setConfirmRemovalOpen(false)}
        />
      )}
    </Page>
  );
};

type ViewRenderOptions = {
  t: TFnType<"OrganisationProfile">;
  projectTypeLabels: Record<OfferedProjectType, string>;
  overriddenByCategory: ReadonlyMap<string, ReadonlySet<OfferedProjectType>>;
  onEditOverride: (categoryId: string, projectType: OfferedProjectType) => void;
};

const CUSTOM_TREE_ITEM_SX = {
  // The tree row itself is not a clickable surface — only the per-project-type
  // chips are interactive (and the chevron, on expandable nodes). Disable the
  // default hover background + pointer cursor on the row.
  "& > .MuiTreeItem-content": {
    cursor: "default",
    "&:hover, &.Mui-focused, &:focus-visible": {
      backgroundColor: "transparent",
    },
  },
};

// Apply only to expandable nodes (those with children) so leaf-row icon slots
// don't get a misleading hover affordance.
const CUSTOM_TREE_ITEM_WITH_EXPAND_BUTTON_HOVER_SX = {
  ...CUSTOM_TREE_ITEM_SX,
  "& > .MuiTreeItem-content .MuiTreeItem-iconContainer": {
    alignItems: "center",
    width: "24px",
    height: "24px",
    cursor: "pointer",
    borderRadius: 2,
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },
};

function renderViewNodes(
  nodes: ViewTreeNode[],
  options: ViewRenderOptions
): React.ReactNode {
  const { t, projectTypeLabels, overriddenByCategory, onEditOverride } =
    options;
  return nodes.map(node => {
    const overridden = overriddenByCategory.get(node.id);
    const isExpandable = node.children.length > 0;
    return (
      <TreeItem
        key={node.id}
        itemId={node.id}
        sx={
          isExpandable
            ? CUSTOM_TREE_ITEM_WITH_EXPAND_BUTTON_HOVER_SX
            : CUSTOM_TREE_ITEM_SX
        }
        label={
          <Stack
            spacing={0.25}
            sx={{
              py: 0.5,
            }}
          >
            <Typography
              variant="h4"
              color={node.declared ? "text.primary" : "text.disabled"}
            >
              {node.name}
            </Typography>
            {node.declared &&
              (node.projectTypes.length > 0 ? (
                <Stack
                  direction="row"
                  spacing={0.5}
                  useFlexGap
                  sx={{
                    flexWrap: "wrap",
                  }}
                >
                  {node.projectTypes.map(pt => {
                    const hasOverride = overridden?.has(pt) ?? false;
                    return (
                      <Tooltip
                        key={pt}
                        title={
                          hasOverride
                            ? t("Customized", { ns: "OrganisationProfile" })
                            : t("Edit override", {
                                ns: "OrganisationProfile",
                              })
                        }
                      >
                        <Chip
                          label={projectTypeLabels[pt]}
                          size="small"
                          variant={hasOverride ? "filled" : "outlined"}
                          color={hasOverride ? undefined : "primary"}
                          sx={
                            hasOverride
                              ? {
                                  backgroundColor: "orange.main",
                                  color: "orange.contrastText",
                                  "&:hover": {
                                    backgroundColor: "orange.dark",
                                  },
                                }
                              : undefined
                          }
                          clickable
                          onClick={() => {
                            onEditOverride(node.id, pt);
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Stack>
              ) : (
                // OFFERED_PROJECT_TYPES excludes OTHER; an empty list is a
                // valid state — OTHER is implicit once the category is declared.
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {t("No project type specified", {
                    ns: "OrganisationProfile",
                  })}
                </Typography>
              ))}
          </Stack>
        }
      >
        {node.children.length > 0 && renderViewNodes(node.children, options)}
      </TreeItem>
    );
  });
}
